import { create } from 'zustand';

/**
 * Keys identifying the independent flows that can raise the blocking loading overlay.
 * The overlay is shown while at least one key is active, so concurrent flows can't tear
 * down each other's overlay — each one only ever releases its own key.
 *
 * ADVENTURE_TRANSITION spans a whole lobby-to-map handoff: the lobby raises it, map
 * creation re-raises it with a new message, and MapScene releases it once the map is on
 * screen. Re-raising an already-active key just updates its message.
 */
export const LoadingKeys = {
  ADVENTURE_TRANSITION: 'adventure-transition',
  MAP_ASSETS: 'map-assets',
  CHARACTER: 'character',
  TRAVEL: 'travel',
  NPC_CONTINUATION: 'npc-continuation',
} as const;

export type LoadingKey = typeof LoadingKeys[keyof typeof LoadingKeys];

// A raiser may omit its message; the overlay then shows only its title and the animated dots.
const NO_LOADING_MESSAGE = '';

type LoadingTasks = Partial<Record<LoadingKey, string>>;

/**
 * A consumable waiting for the player to pick a hex on the map. React raises it from
 * the inventory panel; MapScene watches for it, highlights the map, and clears it once
 * the player either picks a hex or cancels.
 */
export interface HexTargetItem {
  instanceId: string;
  itemName: string;
}

interface UiState {
  loadingTasks: LoadingTasks;
  isLoading: boolean;
  loadingMessage: string;
  startLoading: (key: LoadingKey, message?: string) => void;
  stopLoading: (key: LoadingKey) => void;
  // Global error handling mirrored after loading so React and Phaser can report errors consistently
  errorMessage: string | null;
  setError: (message: string | null) => void;
  clearError: () => void;
  // The consumable awaiting a hex pick, or null when the map is in its normal mode.
  hexTargetItem: HexTargetItem | null;
  startHexTargeting: (item: HexTargetItem) => void;
  cancelHexTargeting: () => void;
}

/**
 * The message shown is the most recently raised one, so the newest flow describes what the
 * player is waiting on.
 */
function deriveLoading(tasks: LoadingTasks): Pick<UiState, 'loadingTasks' | 'isLoading' | 'loadingMessage'> {
  const messages = Object.values(tasks);
  return {
    loadingTasks: tasks,
    isLoading: messages.length > 0,
    loadingMessage: messages[messages.length - 1] ?? NO_LOADING_MESSAGE,
  };
}

export const useUiStore = create<UiState>()((set) => ({
  loadingTasks: {},
  isLoading: false,
  loadingMessage: NO_LOADING_MESSAGE,

  startLoading: (key, message) => set((s) => deriveLoading({
    ...s.loadingTasks,
    [key]: message ?? s.loadingTasks[key] ?? NO_LOADING_MESSAGE,
  })),

  // Releasing a key that isn't active is a no-op, so error/cleanup paths can call it freely.
  stopLoading: (key) => set((s) => {
    if (!(key in s.loadingTasks)) return s;
    const next = { ...s.loadingTasks };
    delete next[key];
    return deriveLoading(next);
  }),

  errorMessage: null,
  setError: (message) => set({ errorMessage: message }),
  clearError: () => set({ errorMessage: null }),

  hexTargetItem: null,
  startHexTargeting: (item) => set({ hexTargetItem: item }),
  cancelHexTargeting: () => set({ hexTargetItem: null }),
}));
